import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Lock, ArrowLeft, ArrowRight, ShieldCheck, KeyRound } from "lucide-react";

const GOLD_LIGHT = "#F5DE8C";
const GOLD = "#D4AF37";
const GOLD_DARK = "#8C6510";

function fmt(d) {
  if (!d) return "Erro";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Se o e-mail existir, um link foi enviado.");
    } catch (err) {
      toast.error(fmt(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <ResetLayout testid="forgot-password-page">
      <form onSubmit={submit} className="hermes-login-card relative w-full max-w-[440px] rounded-2xl px-6 py-8 md:px-8 md:py-9">
        <Link to="/" className="text-neutral-400 hover:text-white text-xs inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5"/> Voltar
        </Link>
        <Brand/>
        <h1 className="text-center font-heading text-2xl font-semibold text-white flex items-center justify-center gap-2">
          <KeyRound className="w-5 h-5" style={{ color: GOLD_LIGHT }}/>
          Esqueceu a senha?
        </h1>
        <p className="text-center text-[13px] text-neutral-300/85 mt-1">
          Digite seu e-mail e enviaremos um link para redefinir.
        </p>

        {sent ? (
          <div className="mt-6 p-4 rounded-xl text-center text-sm text-neutral-200"
               style={{ background: "rgba(230,200,102,0.08)", border: "1px solid rgba(230,200,102,0.35)" }}
               data-testid="forgot-sent-message">
            <ShieldCheck className="w-6 h-6 mx-auto mb-2" style={{ color: GOLD_LIGHT }}/>
            E-mail enviado. Verifique sua caixa de entrada e a pasta de spam.
            O link expira em 1 hora.
          </div>
        ) : (
          <>
            <div className="mt-6">
              <Field icon={Mail} placeholder="seu@e-mail.com" value={email}
                     onChange={setEmail} testid="input-email-forgot" type="email" required/>
            </div>
            <button type="submit" disabled={busy}
              data-testid="btn-submit-forgot"
              className="hermes-cta-primary group mt-5 w-full h-12 rounded-xl inline-flex items-center justify-center gap-2 text-black font-semibold cursor-pointer disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> :
                      <>Enviar link de redefinição <ArrowRight className="w-4 h-4"/></>}
            </button>
          </>
        )}

        <div className="mt-6 text-center text-[12.5px] text-neutral-400">
          Lembrou a senha? <Link to="/login-email" className="hover:text-[#F5DE8C]" style={{ color: GOLD_LIGHT }}>Entrar</Link>
        </div>
      </form>
    </ResetLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (password.length < 8) {
      toast.error("Senha precisa ter ao menos 8 caracteres");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data?.session_token) {
        try { localStorage.setItem("session_token", data.session_token); } catch {}
      }
      toast.success("Senha redefinida! Redirecionando...");
      setTimeout(() => { window.location.href = "/dashboard"; }, 800);
    } catch (err) {
      toast.error(fmt(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <ResetLayout testid="reset-password-page">
      <form onSubmit={submit} className="hermes-login-card relative w-full max-w-[440px] rounded-2xl px-6 py-8 md:px-8 md:py-9">
        <Brand/>
        <h1 className="text-center font-heading text-2xl font-semibold text-white flex items-center justify-center gap-2">
          <KeyRound className="w-5 h-5" style={{ color: GOLD_LIGHT }}/>
          Nova senha
        </h1>
        <p className="text-center text-[13px] text-neutral-300/85 mt-1">
          Escolha uma senha forte com pelo menos 8 caracteres.
        </p>

        {!token ? (
          <div className="mt-6 p-4 rounded-xl text-center text-sm text-rose-300"
               style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.35)" }}>
            Token de redefinição não encontrado. Solicite um novo link.
          </div>
        ) : (
          <>
            <div className="mt-6 space-y-3">
              <Field icon={Lock} placeholder="Nova senha (mín. 8)" value={password}
                     onChange={setPassword} testid="input-password-new" type="password" required/>
              <Field icon={Lock} placeholder="Confirme a senha" value={confirm}
                     onChange={setConfirm} testid="input-password-confirm" type="password" required/>
            </div>
            <button type="submit" disabled={busy}
              data-testid="btn-submit-reset"
              className="hermes-cta-primary group mt-5 w-full h-12 rounded-xl inline-flex items-center justify-center gap-2 text-black font-semibold cursor-pointer disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> :
                      <>Redefinir senha <ArrowRight className="w-4 h-4"/></>}
            </button>
          </>
        )}

        <div className="mt-6 text-center text-[12.5px] text-neutral-400">
          <Link to="/login-email" className="hover:text-[#F5DE8C]" style={{ color: GOLD_LIGHT }}>Voltar ao login</Link>
        </div>
      </form>
    </ResetLayout>
  );
}

function ResetLayout({ children, testid }) {
  return (
    <div className="fixed inset-0 overflow-hidden flex items-center md:justify-end justify-center px-4 md:px-16"
         style={{ background: "#050810" }} data-testid={testid}>
      <img src="/hermes-hero.jpg" alt="" aria-hidden="true" draggable={false}
           className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"/>
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "linear-gradient(90deg, rgba(5,8,16,0.15) 0%, rgba(5,8,16,0.20) 40%, rgba(5,8,16,0.55) 100%)" }}/>
      {children}
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center justify-center gap-3 mb-5">
      <img src="/hermes-wing.png" alt="Hermes" draggable={false}
           className="h-10 w-auto object-contain"
           style={{ filter: "drop-shadow(0 4px 12px rgba(212,175,55,0.35))" }}/>
      <div className="flex flex-col leading-tight">
        <span className="font-heading text-2xl font-bold tracking-tight"
          style={{
            background: `linear-gradient(180deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, ${GOLD_DARK} 100%)`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>HERMES</span>
        <span className="text-[9px] tracking-[0.22em] uppercase mt-0.5"
              style={{ color: "rgba(230,200,102,0.85)" }}>
          Tecnologia Jurídica
        </span>
      </div>
    </div>
  );
}

function Field({ icon: Icon, placeholder, value, onChange, testid, type = "text", required = false }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} type={type} required={required}
        data-testid={testid}
        className="w-full h-11 bg-black/30 text-white rounded-lg pl-9 pr-3 text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
        style={{ border: "1px solid rgba(230,200,102,0.30)" }}/>
    </div>
  );
}
